"use client";

import type { ReactNode } from "react";

import { ShapesIcon, UsersIcon } from "lucide-react";

import { FilterSearchPopover } from "@/components/app/workspace/filter-search-popover";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export type FeedLens = "all" | "relevant";
export type FeedActorFilter = "all" | "me" | "people" | "twin";

export const FEED_KIND_OPTIONS = [
  { value: "all", label: "All types" },
  { value: "post", label: "Posts" },
  { value: "discussion", label: "Discussions" },
  { value: "record", label: "Records" },
  { value: "session", label: "Twin sessions" },
  { value: "artifact", label: "Artifacts" },
  { value: "file", label: "Files" },
  { value: "view", label: "Views" },
  { value: "schema", label: "Data sets" },
] as const;
export type FeedKindFilter = (typeof FEED_KIND_OPTIONS)[number]["value"];

export const FEED_ACTOR_OPTIONS: Array<{
  value: FeedActorFilter;
  label: string;
}> = [
  { value: "all", label: "Everyone" },
  { value: "me", label: "My activity" },
  { value: "people", label: "Teammates" },
  { value: "twin", label: "Twin and systems" },
];

/*
 * The one set of feed controls: a lens (everything, or what's relevant to me),
 * who acted, and what kind of activity. The channel feed and the workspace
 * Feed page render this same bar; the page adds its channel picker in front
 * and its read control at the end. Each surface lists the kinds its stream
 * can contain, so a filter never leads to an empty list by construction.
 */
export function FeedControls({
  lens,
  onLensChange,
  actor,
  onActorChange,
  kind,
  onKindChange,
  kinds,
  leading,
  trailing,
}: {
  lens: FeedLens;
  onLensChange: (lens: FeedLens) => void;
  actor: FeedActorFilter;
  onActorChange: (actor: FeedActorFilter) => void;
  kind: FeedKindFilter;
  onKindChange: (kind: FeedKindFilter) => void;
  kinds: ReadonlyArray<Exclude<FeedKindFilter, "all">>;
  leading?: ReactNode;
  trailing?: ReactNode;
}) {
  const kindOptions = FEED_KIND_OPTIONS.filter(
    (option) => option.value === "all" || kinds.includes(option.value),
  );
  return (
    <div className="mb-2 flex flex-wrap items-center justify-between gap-2 border-b border-border/60 pb-2">
      <Tabs
        value={lens}
        onValueChange={(value) => onLensChange(value as FeedLens)}
      >
        <TabsList variant="navigation" aria-label="Feed lens">
          <TabsTrigger value="all">All activity</TabsTrigger>
          <TabsTrigger value="relevant">Relevant to me</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="flex flex-wrap items-center gap-1.5">
        {leading}
        <FilterSearchPopover
          ariaLabel="Filter feed by actor"
          icon={UsersIcon}
          search=""
          onSearchChange={() => {}}
          searchPlaceholder="Search actors"
          activeLabel={
            FEED_ACTOR_OPTIONS.find((option) => option.value === actor)
              ?.label ?? "Everyone"
          }
          active={actor !== "all"}
          showSearch={false}
          options={FEED_ACTOR_OPTIONS}
          value={actor}
          onValueChange={(value) => onActorChange(value as FeedActorFilter)}
        />
        <FilterSearchPopover
          ariaLabel="Filter feed by type"
          icon={ShapesIcon}
          search=""
          onSearchChange={() => {}}
          searchPlaceholder="Search activity types"
          activeLabel={
            kindOptions.find((option) => option.value === kind)?.label ??
            "All types"
          }
          active={kind !== "all"}
          showSearch={false}
          options={[...kindOptions]}
          value={kind}
          onValueChange={(value) => onKindChange(value as FeedKindFilter)}
        />
        {trailing}
      </div>
    </div>
  );
}
